import { Box, IconButton, Tooltip } from '@mui/material';
import { FilterAltOff } from '@mui/icons-material';
import {
  CustomAutoComplete,
  CustomTextField,
  HorizontalForm,
  renderTwoLineOption,
} from '../../../common';
import { AUTOCOMPLETE_LISTBOX_PROPS } from '../../../common/styles';
import {
  INITIAL_FILTERS,
  SOURCE_TYPE_LABELS,
  SUPPLY_ORDER_FILTER_TEXTS as TEXTS,
  SUPPLY_ORDER_STATUS_LABELS,
} from '../constants/supplyOrdersConstants';
import { toProviderOptions } from '../../utils/toProviderOptions';
import { toWarehouseOptions } from '../../utils/toWarehouseOptions';
import { toProjectOptions } from '../../utils/toProjectOptions';
import { getFilterFieldSx } from '../../utils/getFilterFieldSx';
import { toLabelOptions } from '../utils/toLabelOptions';

const toolbarSx = {
  px: 2.5,
  py: 1.5,
  gap: 1.25,
  alignItems: 'center',
  flexWrap: { xs: 'wrap', lg: 'nowrap' },
  overflowX: 'visible',
};

const STATUS_OPTIONS = toLabelOptions(SUPPLY_ORDER_STATUS_LABELS);
const SOURCE_TYPE_OPTIONS = toLabelOptions(SOURCE_TYPE_LABELS);

/**
 * Toolbar de filtros de órdenes de abastecimiento en una sola fila (en `lg`).
 * @param {Object} props
 * @param {Object} props.filters Filtros vigentes.
 * @param {Array<Object>} [props.warehouses] Depósitos.
 * @param {Array<Object>} [props.providers] Proveedores.
 * @param {Function} props.onFilterChange Recibe el evento de un campo de texto o fecha.
 * @param {Function} props.onSelectChange Recibe el evento de un autocompletar.
 * @returns {JSX.Element}
 */
const SupplyOrdersFilters = ({
  filters,
  warehouses = [],
  providers = [],
  projects = [],
  onFilterChange,
  onSelectChange,
}) => {
  const providerOptions = toProviderOptions(providers);
  const warehouseOptions = toWarehouseOptions(warehouses);

  const handleClear = () => {
    Object.entries(INITIAL_FILTERS).forEach(([name, value]) =>
      onFilterChange({ target: { name, value } })
    );
  };

  return (
    <Box sx={{ backgroundColor: 'background.paper', borderBottom: '1px solid #ccc' }}>
      <HorizontalForm withBorder={false} sx={toolbarSx}>
        <CustomTextField
          label={TEXTS.search}
          name="query"
          value={filters.query}
          onChange={onFilterChange}
          placeholder={TEXTS.searchPlaceholder}
          sx={getFilterFieldSx(1.5, 200)}
        />
        <CustomAutoComplete
          label={TEXTS.status}
          name="status"
          value={filters.status}
          options={STATUS_OPTIONS}
          inputPlaceholder={TEXTS.all}
          onChange={onSelectChange}
          sx={getFilterFieldSx()}
        />
        <CustomAutoComplete
          label={TEXTS.sourceType}
          name="source_type"
          value={filters.source_type}
          options={SOURCE_TYPE_OPTIONS}
          inputPlaceholder={TEXTS.all}
          onChange={onSelectChange}
          sx={getFilterFieldSx()}
        />
        <CustomAutoComplete
          label={TEXTS.provider}
          name="uuid_provider_legal_entity"
          value={filters.uuid_provider_legal_entity}
          options={providerOptions}
          renderOption={renderTwoLineOption}
          inputPlaceholder={TEXTS.searchProvider}
          ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
          onChange={onSelectChange}
          sx={getFilterFieldSx(1.5, 170)}
        />
        <CustomAutoComplete
          label={TEXTS.destination}
          name="uuid_destination_warehouse"
          value={filters.uuid_destination_warehouse}
          options={warehouseOptions}
          renderOption={renderTwoLineOption}
          inputPlaceholder={TEXTS.searchWarehouse}
          ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
          onChange={onSelectChange}
          sx={getFilterFieldSx(1.5, 170)}
        />
        <CustomAutoComplete
          label={TEXTS.project}
          name="uuid_project"
          value={filters.uuid_project || ''}
          options={toProjectOptions(projects)}
          inputPlaceholder={TEXTS.searchProject}
          ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
          onChange={onSelectChange}
          sx={getFilterFieldSx(1.5, 180)}
        />
        <CustomTextField
          label={TEXTS.dateFrom}
          name="date_from"
          type="date"
          value={filters.date_from}
          onChange={onFilterChange}
          shrinkLabel
          sx={getFilterFieldSx()}
        />
        <CustomTextField
          label={TEXTS.dateTo}
          name="date_to"
          type="date"
          value={filters.date_to}
          onChange={onFilterChange}
          shrinkLabel
          sx={getFilterFieldSx()}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Tooltip title={TEXTS.clear}>
            <IconButton color="primary" onClick={handleClear}>
              <FilterAltOff />
            </IconButton>
          </Tooltip>
        </Box>
      </HorizontalForm>
    </Box>
  );
};

export default SupplyOrdersFilters;
